/**
 * Zeichen der Preisautomatik-Schicht (Board „Vertrieb“, „Monat je Apartment“): Preis mit Ziel
 * („104 → 118“, bernstein, noch nicht bestätigt), Mindestnächte mit Ziel („2 N. → 3“), die Marke
 * der Automatik-Übertragung (↗ gesendet, ! fehlgeschlagen) und der Modus je Apartment.
 */
import { Lock } from 'lucide-react';
import type { DynamicPricingMode } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { rate as formatRate } from '../calendar/calendarFormat';
import { MODE_LABEL, type AutomationCell } from './automationModel';

interface PriceProps {
  rate: number | null;
  currency: string;
  manual: boolean;
  prefill: boolean;
  target?: number | null;
  className?: string;
}

export function PriceWithTarget({
  rate,
  currency,
  manual,
  prefill,
  target,
  className,
}: PriceProps) {
  return (
    <span
      className={cn(
        'flex items-center gap-[3px] whitespace-nowrap font-bold tabular-nums',
        target != null ? 'text-amber-ink' : prefill ? 'text-ink-soft' : 'text-ink',
        className,
      )}
    >
      {target != null ? `${rate ?? '—'} → ${target}` : formatRate(rate, currency)}
      {manual && (
        <Lock size={10} strokeWidth={2.6} aria-hidden className="text-ink-soft shrink-0" />
      )}
    </span>
  );
}

export function MinimumStayWithTarget({
  value,
  manual,
  target,
  className,
}: {
  value: number | null;
  manual: boolean;
  target?: number | null;
  className?: string;
}) {
  if (value === null && target == null) return null;
  return (
    <span
      className={cn(
        'flex items-center gap-[3px] font-mono',
        target != null ? 'text-amber-ink font-semibold' : 'text-ink-soft',
        className,
      )}
    >
      {value ?? '—'} N.{target != null && ` → ${target}`}
      {manual && <Lock size={9} strokeWidth={2.6} aria-hidden className="shrink-0" />}
    </span>
  );
}

export function PublicationMark({ cell }: { cell: AutomationCell | null }) {
  if (!cell?.publication) return null;
  const failed = cell.publication === 'failed';
  return (
    <span
      aria-hidden
      className={cn(
        'absolute left-1 top-1 grid size-[15px] place-items-center rounded-full text-[9px] font-extrabold',
        failed ? 'bg-sealwax-tint text-sealwax' : 'bg-amber-tint text-amber-ink',
      )}
    >
      {failed ? '!' : '↗'}
    </span>
  );
}

export function ModeChip({ mode, prefix = '' }: { mode: DynamicPricingMode; prefix?: string }) {
  return (
    <span
      className={cn(
        'rounded-pill inline-flex h-[22px] shrink-0 items-center whitespace-nowrap px-2 text-[11px] font-bold',
        mode === 'Off' ? 'bg-card-alt text-ink-soft' : 'bg-forest-soft text-forest-deep',
      )}
    >
      {prefix}
      {MODE_LABEL[mode]}
    </span>
  );
}

/** Zusatz für den zugänglichen Namen und den Hinweis einer Zelle. */
export function automationText(cell: AutomationCell | null): string {
  if (!cell) return '';
  const parts: string[] = [];
  if (cell.targetRate != null)
    parts.push(`Ziel der Automatik ${cell.targetRate}, noch nicht bestätigt`);
  if (cell.targetMinimumStay != null) parts.push(`Ziel Mindestnächte ${cell.targetMinimumStay}`);
  if (cell.publication === 'sent') parts.push('gesendet, Bestätigung offen');
  if (cell.publication === 'failed') parts.push('Übertragung fehlgeschlagen');
  return parts.length ? ` · ${parts.join(' · ')}` : '';
}
